/**
 * English: Tickets
 *
 * What this file covers: The Tickets menu: queue, board, a ticket, tasks, reports and ticket settings.
 *
 * Tone: Calm, professional helpdesk wording. Keep a clear difference between a reply (the client sees it) and a
 * note (only the team sees it). Status names are short.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const tickets: Catalog = {
  'tickets.title': 'Tickets',
  'tickets.description': 'Every query that needs follow-up, from every channel, in one queue.',
  'tickets.readOnly': 'Only the owner and admins can change these settings. You can see them here.',
  'tickets.me': 'Me',
  'tickets.unassigned': 'Unassigned',
  'tickets.noTeam': 'No team',
  'tickets.noClient': 'No client',
  'tickets.active': 'Active',
  'tickets.inactive': 'Inactive',
  'tickets.default': 'Default',
  'tickets.order': 'Order',
  'tickets.orderHint': 'Lower numbers come first.',
  'tickets.moveUp': 'Move up',
  'tickets.moveDown': 'Move down',
  'tickets.discard.title': 'Discard your changes?',
  'tickets.discard.body': 'What you changed here has not been saved.',
  'tickets.category.new': 'New',
  'tickets.category.open': 'Open',
  'tickets.category.pending': 'Waiting on client',
  'tickets.category.on_hold': 'On hold',
  'tickets.category.solved': 'Solved',
  'tickets.category.closed': 'Closed',
  'tickets.channel.whatsapp': 'WhatsApp',
  'tickets.channel.messenger': 'Messenger',
  'tickets.channel.instagram': 'Instagram',
  'tickets.channel.web': 'Website chat',
  'tickets.channel.email': 'Email',
  'tickets.channel.sms': 'SMS',
  'tickets.channel.voice': 'Phone call',
  'tickets.channel.console': 'Console',
  'tickets.source.console': 'Opened by a person',
  'tickets.source.flow': 'Opened by the chatbot',
  'tickets.source.voice': 'Opened on a phone call',
  'tickets.source.rule': 'Opened by an automation',
  'tickets.view.all': 'All',
  'tickets.view.mine': 'Mine',
  'tickets.view.team': 'My teams',
  'tickets.view.unassigned': 'Unassigned',
  'tickets.view.breaching': 'Breaching soon',
  'tickets.error.PLAN_LIMIT_REACHED': 'Your plan allows no more of these. Upgrade your plan, or switch one off first.',
  'tickets.error.IN_USE': 'Tickets still use this. Move them to another one first.',
  'tickets.error.MESSAGING_WINDOW_CLOSED': 'The reply window on this channel has closed, so a free-text reply cannot be sent.',
  'tickets.error.NO_CONVERSATION': 'This ticket has no conversation to reply on. Link one first.',
  'tickets.error.forbidden': 'You do not have permission to do that.',
  'tickets.error.notFound': 'That ticket could not be found. It may have been merged or you may not have access to it.',
  'tickets.error.unavailable': 'Tickets are not available right now. Try again in a moment.',
  'tickets.duration.minutes': '{{m}} min',
  'tickets.duration.hours': '{{h}} h {{m}} min',
  'tickets.duration.days': '{{d}} d {{h}} h',
  'tickets.field.number': 'Number',
  'tickets.field.subject': 'Subject',
  'tickets.field.type': 'Type',
  'tickets.field.priority': 'Priority',
  'tickets.field.team': 'Team',
  'tickets.field.assignee': 'Assigned to',
  'tickets.field.tag': 'Tag',
  'tickets.field.tags': 'Tags',
  'tickets.field.due': 'Due',
  'tickets.field.dueHint': 'In your business\'s time zone ({{zone}}).',
  'tickets.field.sla': 'Response target',
  'tickets.field.client': 'Client',
  'tickets.field.clientName': 'Client name',
  'tickets.field.channel': 'Channel',
  'tickets.field.source': 'Source',
  'tickets.field.choose': 'Choose…',
  'tickets.field.requiredError': 'This field is required.',
  'tickets.filter.anyStatus': 'Any status',
  'tickets.filter.anyType': 'Any type',
  'tickets.filter.anyPriority': 'Any priority',
  'tickets.filter.anyTeam': 'Any team',
  'tickets.filter.anyone': 'Anyone',
  'tickets.filter.tag': 'Tag',
  'tickets.queue.title': 'Queue',
  'tickets.queue.search': 'Search by number, subject or client',
  'tickets.queue.filtering': 'Filtering',
  'tickets.queue.emptyFiltered': 'No tickets match these filters',
  'tickets.queue.emptyFilteredHint': 'Clear the filters to see every ticket in this view.',
  'tickets.queue.emptyHint': 'Tickets appear here when your team, the chatbot or an automation opens one.',
  'tickets.queue.empty.all': 'No tickets yet',
  'tickets.queue.empty.mine': 'Nothing is assigned to you',
  'tickets.queue.empty.team': 'Your teams have no tickets',
  'tickets.queue.empty.unassigned': 'Every ticket has someone on it',
  'tickets.queue.empty.breaching': 'Nothing is close to its response target',
  'tickets.bulk.selected': 'Selected: {{count}}',
  'tickets.bulk.assign': 'Assign to…',
  'tickets.bulk.status': 'Set status…',
  'tickets.bulk.done': 'Updated {{count}} tickets.',
  'tickets.bulk.someFailed': '{{failed}} of {{total}} tickets were not updated: {{reason}}',
  'tickets.bulk.selectAll': 'Select every ticket shown',
  'tickets.bulk.selectOne': 'Select ticket {{number}}',
  'tickets.create.title': 'New ticket',
  'tickets.create.submit': 'Create ticket',
  'tickets.create.done': 'Ticket {{number}} created.',
  'tickets.create.subjectRequired': 'Give the ticket a subject.',
  'tickets.create.clientNameHint': 'Optional. Tickets opened from a conversation are linked to the client automatically.',
  'tickets.create.notify': 'Tell the client their request was received',
  'tickets.create.notifyHint': 'Sends the ticket number on this conversation.',
  'tickets.create.noTypes': 'There are no active ticket types. The default type is used.',
  'tickets.sla.title': 'Response targets',
  'tickets.sla.firstResponse': 'First response',
  'tickets.sla.resolution': 'Resolution',
  'tickets.sla.dueIn': 'Due in {{time}}',
  'tickets.sla.overdue': 'Overdue',
  'tickets.sla.overdueBy': 'Overdue {{time}}',
  'tickets.sla.paused': 'Paused',
  'tickets.sla.pausedHint': 'The clock is paused while the ticket waits on the client or is on hold.',
  'tickets.sla.breachedHint': 'A response target on this ticket was missed.',
  'tickets.sla.dueAt': '{{phase}} due {{when}}',
  'tickets.sla.dueOn': 'Due {{when}}',
  'tickets.sla.respondedAt': 'Responded {{when}}',
  'tickets.sla.solvedAt': 'Solved {{when}}',
  'tickets.sla.noPolicy': 'No response target applies to this ticket.',
  'tickets.sla.policies': 'Response targets',
  'tickets.sla.policiesIntro': 'How quickly your team promises to respond and resolve, by type and priority.',
  'tickets.sla.add': 'Add a response target',
  'tickets.sla.empty': 'No response targets yet',
  'tickets.sla.emptyHint': 'Add one to see due times and overdue tickets in the queue.',
  'tickets.sla.anyType': 'Any type',
  'tickets.sla.anyPriority': 'Any priority',
  'tickets.sla.clock': 'Clock',
  'tickets.sla.businessHours': 'Business hours',
  'tickets.sla.allHours': 'Around the clock',
  'tickets.sla.firstResponseHint': 'Leave empty for no first-response target.',
  'tickets.sla.resolutionHint': 'Leave empty for no resolution target.',
  'tickets.sla.businessHoursOnly': 'Count business hours only',
  'tickets.sla.businessHoursOnlyHint': 'Time outside your business hours and closures does not count.',
  'tickets.sla.orderHint': 'A ticket gets the first active target, in this order, whose type and priority match.',
  'tickets.sla.error.times': 'Set a first-response or a resolution time.',
  'tickets.unit.label': 'Unit',
  'tickets.unit.minutes': 'Minutes',
  'tickets.unit.hours': 'Hours',
  'tickets.board.title': 'Board',
  'tickets.board.description': 'Tickets by where they stand. Drag a card to another column to change its status.',
  'tickets.board.viewLabel': 'Which tickets',
  'tickets.board.hint': 'Closed tickets leave the board.',
  'tickets.board.empty': 'No tickets here',
  'tickets.board.more': 'Showing the first {{n}}. Use the queue to see the rest.',
  'tickets.board.open': 'Open ticket {{number}}: {{subject}}',
  'tickets.board.moving': 'Moving to {{status}}…',
  'tickets.board.moved': '{{number}} is now {{status}}.',
  'tickets.board.noStatus': 'The type {{type}} has no {{category}} status. Add one under Ticket settings.',
  'tickets.board.chooseTitle': 'Which status?',
  'tickets.board.chooseBody': 'The type {{type}} has more than one status here. Choose one for {{number}}.',
  'tickets.board.moveHere': 'Move',
  'tickets.detail.backToQueue': 'Back to the queue',
  'tickets.detail.saved': 'Saved.',
  'tickets.detail.copyNumber': 'Copy number',
  'tickets.detail.opened': 'Opened {{when}} for {{client}} on {{channel}}',
  'tickets.detail.mergedInto': 'This ticket was merged into another one. Work continues there.',
  'tickets.detail.openMerged': 'Open that ticket',
  'tickets.detail.changeType.title': 'Change the type?',
  'tickets.detail.changeType.body': 'Change this ticket from {{from}} to {{to}}? Its fields and response targets follow the new type.',
  'tickets.detail.changeType.confirm': 'Change type',
  'tickets.detail.noTags': 'No tags',
  'tickets.detail.addTag': 'Add a tag',
  'tickets.detail.removeTag': 'Remove tag {{tag}}',
  'tickets.detail.details': 'Details',
  'tickets.detail.openContact': 'Open contact',
  'tickets.detail.closedAt': 'Closed',
  'tickets.fields.title': '{{type}} fields',
  'tickets.fields.missing': 'Not filled in yet',
  'tickets.csat.title': 'Satisfaction',
  'tickets.csat.score': '{{score}} out of 5',
  'tickets.csat.none': 'No rating yet',
  'tickets.timeline.title': 'Timeline',
  'tickets.timeline.all': 'All',
  'tickets.timeline.messages': 'Messages',
  'tickets.timeline.notes': 'Notes',
  'tickets.timeline.changes': 'Changes',
  'tickets.timeline.empty': 'Nothing here yet.',
  'tickets.timeline.messagesFailed': 'The conversation\'s messages could not be loaded.',
  'tickets.timeline.fromClient': 'From the client',
  'tickets.timeline.toClient': 'To the client',
  'tickets.timeline.note': 'Internal note',
  'tickets.timeline.someone': 'Someone',
  'tickets.timeline.system': 'System',
  'tickets.timeline.template': 'Template: {{name}}',
  'tickets.timeline.attachment': 'Attachment: {{name}}',
  'tickets.timeline.media': 'Media message',
  'tickets.timeline.notDelivered': 'Not delivered',
  'tickets.activity.reply': 'replied to the client',
  'tickets.activity.client_message': 'wrote',
  'tickets.activity.note': 'added a note',
  'tickets.activity.status': 'changed the status',
  'tickets.activity.priority': 'changed the priority',
  'tickets.activity.assignee': 'changed who it is assigned to',
  'tickets.activity.team': 'changed the team',
  'tickets.activity.field': 'changed a field',
  'tickets.activity.task': 'updated a task',
  'tickets.activity.system': 'made a change',
  'tickets.activity.csat': 'rated the service',
  'tickets.activity.merge': 'merged tickets',
  'tickets.activity.link': 'linked a conversation',
  'tickets.composer.reply': 'Reply',
  'tickets.composer.note': 'Note',
  'tickets.composer.replyPlaceholder': 'Write to the client…',
  'tickets.composer.notePlaceholder': 'Write a note for your team…',
  'tickets.composer.replyHint': 'Goes to the client on the linked conversation. Ctrl or Cmd and Enter sends.',
  'tickets.composer.noteHint': 'Only your team sees notes. The client never does.',
  'tickets.composer.send': 'Send reply',
  'tickets.composer.addNote': 'Add note',
  'tickets.composer.sent': 'Reply sent.',
  'tickets.composer.noted': 'Note added.',
  'tickets.composer.on': 'Reply on',
  'tickets.composer.conversation': '{{channel}} conversation {{n}}',
  'tickets.composer.windowClosed.title': 'The reply window has closed',
  'tickets.composer.windowClosed.body': 'It has been too long since the client last wrote on this channel, so only an approved template reaches them now. Send one from the inbox to reopen the conversation.',
  'tickets.composer.windowClosed.action': 'Send a template from the inbox',
  'tickets.tasks.title': 'Tasks',
  'tickets.tasks.add': 'Add task',
  'tickets.tasks.noneOpen': 'No open tasks.',
  'tickets.tasks.showDone': 'Show done tasks ({{count}})',
  'tickets.tasks.hideDone': 'Hide done tasks',
  'tickets.task.kind': 'Kind',
  'tickets.task.kind.call': 'Call',
  'tickets.task.kind.question': 'Ask the client',
  'tickets.task.kind.callout': 'Call-out',
  'tickets.task.kind.custom': 'Other',
  'tickets.task.kindHint.call': 'A to-do with a due time, such as calling the client back.',
  'tickets.task.kindHint.question': 'The question is sent to the client now, and the ticket waits for their reply.',
  'tickets.task.kindHint.callout': 'Books someone to go out to the client, using your bookings.',
  'tickets.task.kindHint.custom': 'Anything else your team needs to do.',
  'tickets.task.defaultTitle.call': 'Call the client back',
  'tickets.task.defaultTitle.question': 'Ask the client a question',
  'tickets.task.defaultTitle.callout': 'Call-out to the client',
  'tickets.task.defaultTitle.custom': 'Follow up',
  'tickets.task.title': 'Task',
  'tickets.task.due': 'Due',
  'tickets.task.question': 'Question for the client',
  'tickets.task.questionHint': 'Sent on the ticket\'s conversation as soon as you add the task.',
  'tickets.task.service': 'Service',
  'tickets.task.person': 'Who goes',
  'tickets.task.anyPerson': 'Whoever is free',
  'tickets.task.address': 'Address',
  'tickets.task.addressHint': 'Where to go. Filled in from the ticket when it has an address.',
  'tickets.task.noServices': 'There are no bookable services yet.',
  'tickets.task.setUpServices': 'Set up services',
  'tickets.task.askNow': 'Ask now',
  'tickets.task.book': 'Book call-out',
  'tickets.task.created': 'Task added: {{title}}',
  'tickets.task.error.title': 'Give the task a name.',
  'tickets.task.error.question': 'Write the question to send.',
  'tickets.task.error.slot': 'Choose a time.',
  'tickets.task.error.address': 'Add the address to go to.',
  'tickets.task.asked': 'Asked: {{question}}',
  'tickets.task.dueWhen': 'Due {{when}}',
  'tickets.task.overdueSince': 'Overdue since {{when}}',
  'tickets.task.noDue': 'No due time',
  'tickets.task.openBooking': 'Open the booking',
  'tickets.task.markDone': 'Mark done',
  'tickets.task.doneTitle': 'Mark the task done',
  'tickets.task.outcome': 'Outcome',
  'tickets.task.outcomeHint': 'Optional. What happened, for the rest of the team.',
  'tickets.task.doneToast': 'Done: {{title}}',
  'tickets.task.doneBy': 'Done by {{who}}, {{when}}',
  'tickets.task.outcomeIs': 'Outcome: {{outcome}}',
  'tickets.task.reopen': 'Reopen',
  'tickets.link.title': 'Conversations',
  'tickets.link.action': 'Link a conversation',
  'tickets.link.none': 'No conversation is linked.',
  'tickets.link.openInInbox': 'Open in the inbox',
  'tickets.link.search': 'Find a conversation',
  'tickets.link.searchHint': 'Search by name, number or the last message.',
  'tickets.link.noMatch': 'No other conversations match.',
  'tickets.link.link': 'Link',
  'tickets.link.done': 'Conversation linked.',
  'tickets.merge.action': 'Merge',
  'tickets.merge.title': 'Merge {{number}} into another ticket',
  'tickets.merge.search': 'Ticket number',
  'tickets.merge.searchHint': 'Type at least two characters of the number or the subject.',
  'tickets.merge.noMatch': 'No open ticket matches.',
  'tickets.merge.warning': '{{from}} moves its activity, tasks and conversations into {{into}} and stops being worked on. This cannot be undone.',
  'tickets.merge.confirm': 'Merge into {{into}}',
  'tickets.merge.done': '{{from}} was merged into {{into}}.',
  'tickets.myTasks.title': 'My tasks',
  'tickets.myTasks.description': 'Calls, questions and call-outs waiting on you, by due time.',
  'tickets.myTasks.mine': 'Mine',
  'tickets.myTasks.everyone': 'Everyone',
  'tickets.myTasks.open': 'Open',
  'tickets.myTasks.all': 'Open and done',
  'tickets.myTasks.overdueCount': 'Overdue: {{count}}',
  'tickets.myTasks.ticket': 'Ticket',
  'tickets.myTasks.done': 'Done',
  'tickets.myTasks.empty': 'No tasks',
  'tickets.myTasks.emptyHint': 'Tasks added to tickets appear here.',
  'tickets.reports.title': 'Ticket reports',
  'tickets.reports.description': 'How many tickets came in and were solved, how quickly, and how clients rated the service.',
  'tickets.reports.last': 'Last {{n}} days',
  'tickets.reports.custom': 'Custom',
  'tickets.reports.from': 'From',
  'tickets.reports.to': 'To',
  'tickets.reports.badRange': 'The end date is before the start date.',
  'tickets.reports.created': 'Created',
  'tickets.reports.solved': 'Solved',
  'tickets.reports.firstResponse': 'First response',
  'tickets.reports.resolution': 'Resolution',
  'tickets.reports.average': 'Average',
  'tickets.reports.slaMet': 'Targets met',
  'tickets.reports.slaMetHint': 'Tickets that met their response targets',
  'tickets.reports.csat': 'Satisfaction',
  'tickets.reports.csatHint': 'Average of {{count}} ratings out of 5',
  'tickets.reports.openNow': 'Open right now',
  'tickets.reports.byDay': 'Created and solved by day',
  'tickets.reports.noDays': 'No tickets in this period',
  'tickets.reports.noDaysHint': 'Choose a longer period, or check back once tickets come in.',
  'tickets.reports.byTeam': 'By team',
  'tickets.reports.byPerson': 'By person',
  'tickets.reports.noRows': 'Nothing to show for this period.',
  'tickets.settings.title': 'Ticket settings',
  'tickets.settings.description': 'Numbering, who sees what, what clients are told, and how tickets are sorted and handled.',
  'tickets.settings.section.general': 'General',
  'tickets.settings.section.updates': 'Client updates',
  'tickets.settings.section.satisfaction': 'Satisfaction',
  'tickets.settings.section.types': 'Types and fields',
  'tickets.settings.section.statuses': 'Statuses',
  'tickets.settings.section.priorities': 'Priorities',
  'tickets.settings.section.sla': 'Response targets',
  'tickets.settings.section.automations': 'Automations',
  'tickets.settings.general': 'General',
  'tickets.settings.prefix': 'Number prefix',
  'tickets.settings.prefixHint': 'Up to eight letters, numbers, hyphens or slashes, such as T-. Tickets are numbered like {{example}}.',
  'tickets.settings.error.prefix': 'Use up to eight letters, numbers, hyphens or slashes.',
  'tickets.settings.error.name': 'Give it a name.',
  'tickets.settings.visibility': 'Who sees which tickets',
  'tickets.settings.visibility.all': 'Everyone sees every ticket',
  'tickets.settings.visibility.team': 'Agents see their teams\' tickets',
  'tickets.settings.visibilityHint': 'With teams, an agent sees their teams\' tickets, tickets assigned to them and tickets with no team. The owner and admins see everything.',
  'tickets.settings.autoClose': 'Close solved tickets after (days)',
  'tickets.settings.autoCloseHint': 'Leave empty or 0 to keep solved tickets until someone closes them.',
  'tickets.settings.reopen': 'Reopen a solved ticket when the client replies',
  'tickets.settings.reopenHint': 'A closed ticket is never reopened. An automation can open a new one instead.',
  'tickets.settings.updates': 'Client updates',
  'tickets.settings.updatesIntro': 'What clients are told about their ticket, on the conversation it came from.',
  'tickets.settings.notifyCreated': 'When a ticket is opened',
  'tickets.settings.notifyCreatedHint': 'Sends the ticket number so the client can quote it.',
  'tickets.settings.notifyStatus': 'When the status changes',
  'tickets.settings.notifyStatusHint': 'Sends the new status\'s client wording.',
  'tickets.settings.notifySolved': 'When the ticket is solved',
  'tickets.settings.notifySolvedHint': 'Sends the solved status\'s client wording.',
  'tickets.settings.updatesHint': 'Updates go in English or Afrikaans, by your business\'s language. A message that cannot be delivered is noted on the ticket.',
  'tickets.settings.satisfaction': 'Satisfaction',
  'tickets.settings.satisfactionIntro': 'Ask the client to rate the service when their ticket is solved.',
  'tickets.settings.csatEnabled': 'Ask for a rating on solve',
  'tickets.settings.csatQuestion': 'The question',
  'tickets.settings.csatDefault': 'How did we do? Reply with a number from 1 (poor) to 5 (excellent).',
  'tickets.settings.csatQuestionHint': 'Leave empty to use the question shown. A reply of 1 to 5 within three days is recorded.',
  'tickets.settings.fixFirst': 'Fix the highlighted fields first.',
  'tickets.settings.toast.saved': 'Ticket settings saved.',
  'tickets.settings.toast.savedNamed': '{{name}} saved.',
  'tickets.settings.toast.deleted': '{{name}} deleted.',
  'tickets.settings.delete.title': 'Delete {{name}}?',
  'tickets.settings.delete.body': '{{name}} will be deleted. This cannot be undone.',
  'tickets.settings.deleteNamed': 'Delete {{name}}',
  'tickets.settings.limits': 'Plan limits',
  'tickets.settings.limitTypes': 'Ticket types: up to {{n}}',
  'tickets.settings.limitTypesNone': 'Ticket types: no limit',
  'tickets.settings.limitRules': 'Active automations: up to {{n}}',
  'tickets.settings.limitRulesNone': 'Active automations: no limit',
  'tickets.settings.limitsHint': 'Upgrade your plan under Billing for more.',
  'tickets.types.title': 'Types and fields',
  'tickets.types.intro': 'Kinds of ticket, each with its own fields, default team and priority.',
  'tickets.types.introLimit': 'Kinds of ticket, each with its own fields. Using {{used}} of {{limit}}.',
  'tickets.types.limitHint': 'Active types: {{active}}. Inactive types still count towards the limit until deleted.',
  'tickets.types.add': 'Add type',
  'tickets.types.new': 'New ticket type',
  'tickets.types.create': 'Create type',
  'tickets.types.empty': 'No ticket types yet',
  'tickets.types.emptyHint': 'A general type is made for you on first use.',
  'tickets.types.fields': 'Fields',
  'tickets.types.fieldsHint': 'Information your team fills in on tickets of this type.',
  'tickets.types.noFields': 'No fields. Tickets of this type have a subject and description only.',
  'tickets.types.fieldLabel': 'Label',
  'tickets.types.fieldKey': 'Key: {{key}}',
  'tickets.types.options': 'Options',
  'tickets.types.optionsHint': 'Separate the options with commas.',
  'tickets.types.clientVisible': 'The client may be told',
  'tickets.types.removeField': 'Remove the field {{label}}',
  'tickets.types.addField': 'Add a field',
  'tickets.types.colour': 'Colour',
  'tickets.types.descriptionHint': 'For your team: when to use this type.',
  'tickets.types.defaultTeam': 'Default team',
  'tickets.types.defaultTeamHint': 'New tickets of this type go to this team.',
  'tickets.types.defaultPriority': 'Default priority',
  'tickets.types.orgDefault': 'The usual default',
  'tickets.types.activeHint': 'An inactive type is not offered for new tickets.',
  'tickets.types.delete.title': 'Delete this type?',
  'tickets.types.delete.body': '{{name}} will no longer be offered. Tickets that have it keep it.',
  'tickets.types.toast.saved': '{{name}} saved.',
  'tickets.types.toast.created': '{{name}} created.',
  'tickets.types.toast.deleted': '{{name}} deleted.',
  'tickets.types.error.name': 'Give the type a name.',
  'tickets.types.error.fieldLabel': 'Every field needs a label.',
  'tickets.types.error.options': 'A list field needs at least two options.',
  'tickets.fieldType.text': 'Text',
  'tickets.fieldType.number': 'Number',
  'tickets.fieldType.date': 'Date',
  'tickets.fieldType.select': 'One from a list',
  'tickets.fieldType.multi_select': 'Several from a list',
  'tickets.fieldType.yes_no': 'Yes or no',
  'tickets.fieldType.address': 'Address',
  'tickets.statuses.title': 'Statuses',
  'tickets.statuses.intro': 'Each status belongs to a stage. Clients see the client wording, never the name.',
  'tickets.statuses.add': 'Add status',
  'tickets.statuses.none': 'No statuses at this stage.',
  'tickets.statuses.category': 'Stage',
  'tickets.statuses.clientLabel': 'Client wording',
  'tickets.statuses.clientLabelHint': 'What the client is told, such as "We are working on it".',
  'tickets.statuses.forType': 'For',
  'tickets.statuses.everyType': 'Every type',
  'tickets.statuses.isDefault': 'Default for new tickets',
  'tickets.statuses.isDefaultHint': 'New tickets start with this status.',
  'tickets.statuses.orderHint': 'The first status of a stage is used when a ticket moves to it.',
  'tickets.statuses.categoryHint.new': 'Just opened. Nobody has looked yet.',
  'tickets.statuses.categoryHint.open': 'Your team is working on it.',
  'tickets.statuses.categoryHint.pending': 'Waiting for the client. The response clock is paused.',
  'tickets.statuses.categoryHint.on_hold': 'Waiting on something else. The response clock is paused.',
  'tickets.statuses.categoryHint.solved': 'Done. The client can still reply.',
  'tickets.statuses.categoryHint.closed': 'Finished for good.',
  'tickets.priorities.title': 'Priorities',
  'tickets.priorities.intro': 'How urgent a ticket is. Response targets can differ by priority.',
  'tickets.priorities.add': 'Add priority',
  'tickets.priorities.empty': 'No priorities yet',
  'tickets.priorities.emptyHint': 'Low, Normal, High and Urgent are made for you on first use.',
  'tickets.priorities.rank': 'Rank',
  'tickets.priorities.rankHint': 'Higher is more urgent.',
  'tickets.priorities.colour': 'Colour',
  'tickets.priorities.isDefault': 'Default',
  'tickets.priorities.isDefaultHint': 'New tickets get this priority unless their type says otherwise.',
  'tickets.rules.title': 'Automations',
  'tickets.rules.intro': 'Rules that open, sort and follow up tickets by themselves. They are checked from the top down.',
  'tickets.rules.limit': 'Active: {{active}} of {{limit}}',
  'tickets.rules.noLimit': 'Active: {{active}}',
  'tickets.rules.add': 'Add automation',
  'tickets.rules.new': 'New automation',
  'tickets.rules.create': 'Create automation',
  'tickets.rules.empty': 'No automations yet',
  'tickets.rules.emptyHint': 'For example: open a ticket when a client writes "refund", or remind the team when a ticket waits too long.',
  'tickets.rules.orderHint': 'Drag the rules, or use the arrows, to change the order.',
  'tickets.rules.stops': 'Stops here',
  'tickets.rules.switchFor': 'Switch {{name}} on or off',
  'tickets.rules.runs': 'Recent runs',
  'tickets.rules.recentRuns': 'Recent runs',
  'tickets.rules.noRuns': 'This automation has not run yet.',
  'tickets.rules.outcome.done': 'Done',
  'tickets.rules.outcome.skipped': 'Skipped',
  'tickets.rules.outcome.failed': 'Failed',
  'tickets.rules.conditionsCount': 'Conditions: {{count}}',
  'tickets.rules.always': 'Always',
  'tickets.rules.then': 'Then: {{actions}}',
  'tickets.rules.deleteBody': '{{name}} will stop running and be deleted. This cannot be undone.',
  'tickets.rules.toast.on': '{{name}} is on.',
  'tickets.rules.toast.off': '{{name}} is off.',
  'tickets.rules.toast.saved': '{{name}} saved.',
  'tickets.rules.toast.created': '{{name}} created.',
  'tickets.rules.when': 'When',
  'tickets.rules.if': 'If',
  'tickets.rules.thenDo': 'Then',
  'tickets.rules.allMustMatch': 'Every condition must match.',
  'tickets.rules.noConditions': 'No conditions: it runs every time.',
  'tickets.rules.addCondition': 'Add a condition',
  'tickets.rules.hiddenConditions': 'Conditions that do not apply to this trigger are left out: {{count}}',
  'tickets.rules.field': 'Field',
  'tickets.rules.op': 'Comparison',
  'tickets.rules.value': 'Value',
  'tickets.rules.removeCondition': 'Remove this condition',
  'tickets.rules.removeAction': 'Remove this action',
  'tickets.rules.noActions': 'No actions yet.',
  'tickets.rules.addAction': 'Add an action…',
  'tickets.rules.minutes': 'Waiting for (minutes)',
  'tickets.rules.minutesHint': 'How long the client has waited with no reply from a person or the chatbot.',
  'tickets.rules.minutesUnit': 'In minutes.',
  'tickets.rules.keywordsPlaceholder': 'refund, broken, cancel my order',
  'tickets.rules.keywordsHint': 'Separate with commas. Whole words or phrases; capitals do not matter.',
  'tickets.rules.listPlaceholder': 'First, second',
  'tickets.rules.listHint': 'Separate with commas.',
  'tickets.rules.nothingToPick': 'Nothing to choose from yet.',
  'tickets.rules.firstType': 'The first type',
  'tickets.rules.typeDefault': 'The type\'s default',
  'tickets.rules.subjectHint': 'You can use {{text}}, {{contact.name}} and {{channel}}.',
  'tickets.rules.keepTeam': 'Keep the team',
  'tickets.rules.person': 'Person',
  'tickets.rules.nobodyInParticular': 'Nobody in particular',
  'tickets.rules.roundRobin': 'Take turns within the team',
  'tickets.rules.roundRobinHint': 'Assigns the team member with the fewest open tickets.',
  'tickets.rules.whichField': 'Field',
  'tickets.rules.notifyWho': 'Who to tell',
  'tickets.rules.notify.assignee': 'Whoever it is assigned to',
  'tickets.rules.notify.team': 'The ticket\'s team',
  'tickets.rules.notify.user': 'A particular person',
  'tickets.rules.replyText': 'Message to the client',
  'tickets.rules.replyHint': 'Sent on the ticket\'s conversation.',
  'tickets.rules.dueIn': 'Due after',
  'tickets.rules.closeHint': 'Moves the ticket to its first closed status.',
  'tickets.rules.stopAfter': 'Stop after this automation',
  'tickets.rules.stopAfterHint': 'When this one runs, the automations below it are not checked.',
  'tickets.rules.active': 'On',
  'tickets.rules.activeHint': 'Only automations that are on run. Your plan limits how many can be on.',
  'tickets.rules.error.name': 'Give the automation a name.',
  'tickets.rules.error.minutes': 'Set how many minutes to wait.',
  'tickets.rules.error.condition': 'Finish or remove the highlighted conditions.',
  'tickets.rules.error.noActions': 'Add at least one action.',
  'tickets.rules.error.createTicketTrigger': 'Only an automation that starts from a message can open a ticket.',
  'tickets.rules.error.assign': 'Choose a team or a person.',
  'tickets.rules.error.status': 'Choose a status.',
  'tickets.rules.error.priority': 'Choose a priority.',
  'tickets.rules.error.tag': 'Type the tag.',
  'tickets.rules.error.field': 'Choose a field.',
  'tickets.rules.error.notifyUser': 'Choose who to tell.',
  'tickets.rules.error.text': 'Write the message.',
  'tickets.rules.error.taskTitle': 'Give the task a name.',
  'tickets.trigger.message_received': 'A client sends a message',
  'tickets.trigger.unanswered': 'A client is left without a reply',
  'tickets.trigger.ticket_created': 'A ticket is opened',
  'tickets.trigger.ticket_updated': 'A ticket changes',
  'tickets.trigger.time_passed': 'Time passes on a ticket',
  'tickets.trigger.hint.message_received': 'Every message from a client, on every channel.',
  'tickets.trigger.hint.unanswered': 'A conversation where the client\'s last message has had no reply for the minutes below.',
  'tickets.trigger.hint.ticket_created': 'Right after a ticket is opened, by anyone or anything.',
  'tickets.trigger.hint.ticket_updated': 'Whenever a ticket\'s status, priority, team, assignee, fields or tags change.',
  'tickets.trigger.hint.time_passed': 'Checked every five minutes. Use "Minutes in this status" to say how long.',
  'tickets.op.eq': 'is',
  'tickets.op.neq': 'is not',
  'tickets.op.in': 'is any of',
  'tickets.op.contains_any': 'contains any of',
  'tickets.op.not_contains_any': 'contains none of',
  'tickets.op.is_set': 'is set',
  'tickets.op.not_set': 'is not set',
  'tickets.op.gte': 'is at least',
  'tickets.op.lte': 'is at most',
  'tickets.cond.channel': 'Channel',
  'tickets.cond.text': 'Message text',
  'tickets.cond.contact.tag': 'Contact tag',
  'tickets.cond.business_hours': 'Business hours',
  'tickets.cond.ticket.type_id': 'Ticket type',
  'tickets.cond.ticket.priority_id': 'Ticket priority',
  'tickets.cond.ticket.status_category': 'Ticket stage',
  'tickets.cond.ticket.team_id': 'Ticket team',
  'tickets.cond.ticket.tag': 'Ticket tag',
  'tickets.cond.minutes_in_status': 'Minutes in this status',
  'tickets.cond.customField': 'Field: {{label}}',
  'tickets.cond.hoursOpen': 'Open',
  'tickets.cond.hoursClosed': 'Closed',
  'tickets.action.create_ticket': 'Open a ticket',
  'tickets.action.assign': 'Assign',
  'tickets.action.set_status': 'Set the status',
  'tickets.action.set_priority': 'Set the priority',
  'tickets.action.add_tag': 'Add a tag',
  'tickets.action.set_field': 'Fill in a field',
  'tickets.action.notify': 'Tell someone',
  'tickets.action.reply_client': 'Reply to the client',
  'tickets.action.add_task': 'Add a task',
  'tickets.action.close': 'Close the ticket'
};

export default tickets;
